import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import { menuItemService } from '../../../../services/menuItemService';
import { menuCategoryService } from '../../../../services/menuCategoryService';
import { restaurantOrderService } from '../../../../services/restaurantOrderService';
import happyHourService from '../../../../services/happyHourService';
import kotVoidReasonService from '../../../../services/kotVoidReasonService';
import { outletService } from '../../../../services/outletService';
import { restaurantTableService } from '../../../../services/restaurantTableService';
import { companySettingsService } from '../../../../services/companySettingsService';
import { useCurrency } from '../../../../hooks/useCurrency';
import paymentTypeService, { PaymentType } from '../../../../services/paymentTypeService';
import { useAuth } from '../../../../context/AuthContext';
import { Permissions } from '../../../../constants/Permissions';
import {
  MenuItemListDTO, MenuCategoryDTO, OutletListDTO,
  RestaurantOrderDTO, RestaurantOrderListDTO,
  CompanySettings, BillItemLineFormat, BillPaperSize,
  IssueKotResponseDTO, ActiveHappyHourDTO, KotTicketLineDTO, KotVoidReasonDTO,
} from '../../../../types';
import {
  Search, Plus, Minus, Trash2, ShoppingCart,
  Coffee, Utensils, ChefHat, Printer, CreditCard,
  X, UtensilsCrossed, ChevronLeft, ChevronRight, Tag, Check,
  User, History, ArrowUpDown,
  ArrowLeft, Banknote, Smartphone, Gift, Star,
  Mail, Eye, FileText, Monitor, Wallet, ShoppingBag, Lock,
  MapPin, Users, RefreshCw, LayoutGrid,
} from 'lucide-react';
import './RestaurantOrderPage.css';

/* ── Types ───────────────────────────────────────────────────────────────── */

const PAGE_SIZE = 12;

interface OrderItem {
  itemId: number;
  name: string;
  price: number;
  quantity: number;
  categoryName?: string;
  menuCategoryId?: number;
  taxRate?: number;
  taxType?: string;
  kotBotType?: string;
  imageUrl?: string;
  discountAmount: number;
}

type OrderType = 'Dine In' | 'Takeout' | 'Delivery';

interface PaymentEntry {
  method: string;
  category: string;
  amount: number;
  reference?: string;
}
const ORDER_TYPES: OrderType[] = ['Dine In', 'Takeout', 'Delivery'];
const CAT_ICONS = [Coffee, Utensils, ChefHat, UtensilsCrossed];
type SortBy = 'popular' | 'name' | 'price-asc' | 'price-desc';

// TABLE_OPTIONS kept as fallback when API tables haven't loaded yet
const FALLBACK_TABLE_OPTIONS = [
  { id: 0, value: '', label: 'No Table (Takeout)' },
  ...Array.from({ length: 10 }, (_, i) => ({ id: 0, value: `Table ${i + 1}`, label: `Table ${i + 1}` })),
];

const PAX_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12];

/* ── Component ───────────────────────────────────────────────────────────── */

interface RestaurantOrderPageProps {
  cashierMode?: boolean;
}

const RestaurantOrderPage: React.FC<RestaurantOrderPageProps> = ({ cashierMode = false }) => {
  const navigate = useNavigate();
  const currencyCode = useCurrency();
  const fmt = useMemo(() => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode });
    } catch {
      return new Intl.NumberFormat(undefined, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }, [currencyCode]);

  const fmtNum = useMemo(
    () => new Intl.NumberFormat(undefined, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    []
  );

  const { hasPermission } = useAuth();
  const canIssueKot = hasPermission(Permissions.Orders.IssueKot);
  const canVoidKot  = hasPermission(Permissions.Orders.VoidKot);

  /* ── Company / bill config ── */
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);

  /* ── Menu data ── */
  const [categories, setCategories]   = useState<MenuCategoryDTO[]>([]);
  const [menuItems,  setMenuItems]    = useState<MenuItemListDTO[]>([]);
  const [outlets,    setOutlets]      = useState<OutletListDTO[]>([]);
  const [outletId,   setOutletId]     = useState<number | ''>('');
  const [selCatId,   setSelCatId]     = useState<number | null>(null);
  const [search,     setSearch]       = useState('');
  const [loading,    setLoading]      = useState(false);
  const [menuPage,   setMenuPage]     = useState(0);
  const [sortBy,     setSortBy]       = useState<SortBy>('popular');

  /* ── Per-card local qty ── */
  const [localQtys, setLocalQtys] = useState<Record<number, number>>({});
  const getLocalQty  = (id: number) => localQtys[id] ?? 1;
  const changeLocalQty = (id: number, delta: number) =>
    setLocalQtys(prev => ({ ...prev, [id]: Math.max(1, (prev[id] ?? 1) + delta) }));

  /* ── Tables (loaded from API) ── */
  const [apiTables, setApiTables] = useState<{ id: number; value: string; label: string }[]>([]);
  const [rawTables, setRawTables] = useState<any[]>([]);
  const [showFloorPlan, setShowFloorPlan] = useState(false);

  /* ── Order state ── */
  const [orderItems,   setOrderItems]   = useState<OrderItem[]>([]);
  const [orderType,    setOrderType]    = useState<OrderType>('Dine In');
  const [tableId,      setTableId]      = useState<number>(0);
  const [tableNumber,  setTableNumber]  = useState('');
  const [numberOfPax,  setNumberOfPax]  = useState(1);
  const [customerName, setCustomerName] = useState('');
  const [activeOrderId, setActiveOrderId] = useState<number | null>(null);
  // Payment is gated on the kitchen having the order: true once a KOT/BOT
  // ticket has been issued (this session or on a resumed order).
  const [kotIssued, setKotIssued] = useState(false);
  // Quantities already sent to the kitchen/bar per menu item id. Subsequent
  // KOT/BOT prints send only the delta (new items / increased quantities),
  // so the kitchen never receives duplicate full tickets.
  const [sentQtys, setSentQtys] = useState<Record<number, number>>({});

  /* ── New Order modal ── */
  const [showNewOrderModal, setShowNewOrderModal] = useState(true);
  const [modalOrderType,    setModalOrderType]    = useState<OrderType>('Dine In');
  const [modalTable,        setModalTable]        = useState('');
  const [modalTableId,      setModalTableId]      = useState<number>(0);
  const [modalPax,          setModalPax]          = useState(1);

  /* ── Open Orders panel ── */
  const [showOpenOrders,    setShowOpenOrders]    = useState(false);
  const [openOrders,        setOpenOrders]        = useState<RestaurantOrderListDTO[]>([]);
  const [openOrdersLoading, setOpenOrdersLoading] = useState(false);

  /* ── Item discount editing ── */
  const [editDiscId, setEditDiscId] = useState<number | null>(null);
  const [discInput,  setDiscInput]  = useState('');

  /* ── Order-level discount ── */
  const [discType,  setDiscType]  = useState<'percent' | 'flat'>('percent');
  const [discValue, setDiscValue] = useState('');

  /* ── Settlement modal ── */
  const [showSettle,      setShowSettle]      = useState(false);
  const [paymentTypes,    setPaymentTypes]    = useState<PaymentType[]>([]);
  const [selPayMethod,    setSelPayMethod]    = useState('');
  const [payEntryAmount,  setPayEntryAmount]  = useState('');
  const [payEntryRef,     setPayEntryRef]     = useState('');
  const [paymentEntries,  setPaymentEntries]  = useState<PaymentEntry[]>([]);
  const [saving,          setSaving]          = useState(false);
  const [settleError,     setSettleError]     = useState<string | null>(null);

  /* ── Last settled order ── */
  const [lastOrder, setLastOrder] = useState<RestaurantOrderDTO | null>(null);

  /* ── Happy Hour ── */
  const [activeHappyHour, setActiveHappyHour] = useState<ActiveHappyHourDTO | null>(null);

  /* ── Void KOT items modal ── */
  const [voidModalItem,   setVoidModalItem]   = useState<OrderItem | null>(null);
  const [voidReason,      setVoidReason]      = useState('');
  const [voiding,         setVoiding]         = useState(false);
  const [kotVoidReasons,  setKotVoidReasons]  = useState<KotVoidReasonDTO[]>([]);

  const loadTables = async () => {
    try {
      const res = await restaurantTableService.getAll();
      const d = (res.data as any)?.data;
      const arr = Array.isArray(d) ? d : (d?.data ?? []);
      const active = arr.filter((t: any) => t.isActive !== false);
      setRawTables(active);
      setApiTables([
        { id: 0, value: '', label: 'No Table (Takeout)' },
        ...active.map((t: any) => ({
          id: t.id,
          value: t.tableNumber,
          label: `${t.tableNumber} – ${t.name} (${t.capacity}p)${t.section ? ' [' + t.section + ']' : ''}`,
        })),
      ]);
    } catch { /* fallback to FALLBACK_TABLE_OPTIONS */ }
  };

  /* ── Load everything on mount ── */
  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadCategories(),
      loadAllItems(),
      loadOutlets(),
      loadPaymentTypes(),
      loadCompanySettings(),
      loadHappyHour(),
      loadTables(),
    ]).finally(() => setLoading(false));

    // Re-check active happy hour every minute
    const hhInterval = setInterval(loadHappyHour, 60_000);
    return () => clearInterval(hhInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setMenuPage(0); }, [selCatId, search, sortBy]);

  const loadCategories = async () => {
    try {
      const res = await menuCategoryService.getAll();
      const d = (res.data as any)?.data;
      setCategories(Array.isArray(d) ? d : (d?.data ?? []));
    } catch { /* non-fatal */ }
  };

  const loadOutlets = async () => {
    try {
      const res = await outletService.getAll();
      const d = (res.data as any)?.data;
      const list: OutletListDTO[] = Array.isArray(d) ? d : (d?.data ?? []);
      setOutlets(list);
      if (list.length > 0) setOutletId(list[0].id);
    } catch { /* non-fatal */ }
  };

  const loadCompanySettings = async () => {
    try {
      const res = await companySettingsService.getCompanySettings();
      const d = (res.data as any)?.data ?? res.data;
      if (d) setCompanySettings(d);
    } catch { /* non-fatal */ }
  };

  const loadHappyHour = async () => {
    try {
      const res = await happyHourService.getActive();
      const d = (res.data as any)?.data ?? null;
      setActiveHappyHour(d);
    } catch { /* non-fatal */ }
  };

  const isHappyHourEligible = (itemId: number, menuCategoryId?: number): boolean => {
    if (!activeHappyHour) return false;
    const { appliesTo, categoryIds, menuItemIds } = activeHappyHour;
    return appliesTo === 'AllItems' ||
      (appliesTo === 'SelectedCategories' && menuCategoryId !== undefined && categoryIds.includes(menuCategoryId)) ||
      (appliesTo === 'SelectedItems' && menuItemIds.includes(itemId));
  };

  const calcHappyHourDiscount = (price: number, qty: number): number => {
    if (!activeHappyHour) return 0;
    const { discountType, discountValue } = activeHappyHour;
    const lineTotal = price * qty;
    const disc = discountType === 'Percentage'
      ? lineTotal * (discountValue / 100)
      : Math.min(discountValue, lineTotal);
    return Math.round(disc * 100) / 100;
  };

  const loadAllItems = async () => {
    try {
      const res = await menuItemService.getAll(1, 9999);
      const raw = (res.data as any)?.data ?? (res.data as any) ?? [];
      setMenuItems(Array.isArray(raw) ? raw : (raw?.data ?? []));
    } catch { /* non-fatal */ }
  };

  const loadPaymentTypes = async () => {
    try {
      const types = await paymentTypeService.getActivePaymentTypes('Sales');
      const list = types ?? [];
      setPaymentTypes(list);
      const def = list.find((t: PaymentType) => t.isDefault) ?? list[0];
      if (def) setSelPayMethod(def.name);
    } catch { /* non-fatal */ }
  };

  const loadOpenOrders = async () => {
    setOpenOrdersLoading(true);
    try {
      const res = await restaurantOrderService.getAll(1, 100, 'Open');
      const d = (res.data as any)?.data;
      const list: RestaurantOrderListDTO[] = Array.isArray(d) ? d : (d?.data ?? []);
      setOpenOrders(list);
    } catch { /* non-fatal */ }
    finally { setOpenOrdersLoading(false); }
  };

  const handleCategory = (id: number | null) => { setSelCatId(id); };

  /* ── Filtered + sorted + paginated menu items ── */
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return menuItems.filter(i =>
      i.isActive &&
      (selCatId === null || i.menuCategoryId === selCatId) &&
      (!q || i.name.toLowerCase().includes(q) || (i.categoryName ?? '').toLowerCase().includes(q))
    );
  }, [menuItems, selCatId, search]);

  const sortedItems = useMemo(() => {
    const items = [...filteredItems];
    switch (sortBy) {
      case 'name':       return items.sort((a, b) => a.name.localeCompare(b.name));
      case 'price-asc':  return items.sort((a, b) => a.price - b.price);
      case 'price-desc': return items.sort((a, b) => b.price - a.price);
      default:           return items;
    }
  }, [filteredItems, sortBy]);

  const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE) || 1;
  const paginatedItems = sortedItems.slice(menuPage * PAGE_SIZE, (menuPage + 1) * PAGE_SIZE);

  /* ── Order helpers ── */
  const addItem = (item: MenuItemListDTO) => {
    if (!item.isAvailable) return;
    const qty = getLocalQty(item.id);
    const eligible = isHappyHourEligible(item.id, item.menuCategoryId);
    const discountAmount = eligible ? calcHappyHourDiscount(item.price, qty) : 0;
    setOrderItems(prev => {
      const exists = prev.find(o => o.itemId === item.id);
      if (exists) return prev.map(o => o.itemId === item.id ? { ...o, quantity: o.quantity + qty } : o);
      return [...prev, {
        itemId: item.id,
        name: item.name,
        price: item.price,
        quantity: qty,
        categoryName: item.categoryName,
        menuCategoryId: item.menuCategoryId,
        taxRate: item.taxRate,
        taxType: item.taxType,
        kotBotType: item.kotBotType,
        imageUrl: item.imageUrl,
        discountAmount,
      }];
    });
    setLocalQtys(prev => ({ ...prev, [item.id]: 1 }));
  };

  const changeQty = (itemId: number, delta: number) =>
    setOrderItems(prev => {
      const sent = sentQtys[itemId] ?? 0;
      return prev
        .map(o => {
          if (o.itemId !== itemId) return o;
          // Cannot reduce below already-sent quantity — use void flow instead
          const newQty = o.quantity + delta;
          if (newQty < sent) return o;
          return { ...o, quantity: newQty };
        })
        .filter(o => o.quantity > 0);
    });

  const applyItemDiscount = (itemId: number) => {
    const val = parseFloat(discInput || '0') || 0;
    setOrderItems(prev =>
      prev.map(o => o.itemId === itemId
        ? { ...o, discountAmount: Math.max(0, Math.min(val, o.price * o.quantity)) }
        : o)
    );
    setEditDiscId(null);
    setDiscInput('');
  };

  const clearOrder = () => {
    setOrderItems([]);
    setDiscValue('');
    setDiscType('percent');
    setEditDiscId(null);
    setLastOrder(null);
    setTableId(0);
    setTableNumber('');
    setCustomerName('');
    setActiveOrderId(null);
    setKotIssued(false);
    setSentQtys({});
    setNumberOfPax(1);
    setShowNewOrderModal(true);
    setModalOrderType('Dine In');
    setModalTable('');
    setModalTableId(0);
    setModalPax(1);
  };

  /* ── Resume an existing open order ── */
  const resumeOrder = async (order: RestaurantOrderListDTO) => {
    try {
      const res = await restaurantOrderService.getById(order.id);
      const full: RestaurantOrderDTO = (res.data as any)?.data ?? res.data;
      setActiveOrderId(full.id);
      setKotIssued(full.kotIssued ?? false);
      // Use persisted SentQty from each order line — this is the authoritative source
      // after the backend was updated to store it. Fall back to ticket reconstruction
      // for older orders that predate the SentQty column.
      if (full.lines.some(l => l.sentQty > 0)) {
        setSentQtys(Object.fromEntries(full.lines.map(l => [l.menuItemId, l.sentQty])));
      } else if (full.kotTickets && full.kotTickets.length > 0) {
        const kotSum: Record<number, number> = {};
        const botSum: Record<number, number> = {};
        full.kotTickets.filter(t => !t.isVoid).forEach(t => {
          const bucket = t.ticketType === 'BOT' ? botSum : kotSum;
          t.lines.forEach(l => { bucket[l.menuItemId] = (bucket[l.menuItemId] ?? 0) + l.quantity; });
        });
        const sent: Record<number, number> = {};
        new Set([...Object.keys(kotSum), ...Object.keys(botSum)]).forEach(k => {
          const id = Number(k);
          sent[id] = Math.max(kotSum[id] ?? 0, botSum[id] ?? 0);
        });
        setSentQtys(sent);
      } else if (full.kotIssued) {
        setSentQtys(Object.fromEntries(full.lines.map(l => [l.menuItemId, l.quantity])));
      } else {
        setSentQtys({});
      }
      setOrderType(full.orderType === 'DineIn' ? 'Dine In' : full.orderType as OrderType);
      setTableId(full.tableId ?? 0);
      setTableNumber(full.tableNumber ?? '');
      setNumberOfPax(full.numberOfPax ?? 1);
      setCustomerName(full.customerName ?? '');
      setOrderItems(full.lines.map(l => {
        const mi = menuItems.find(m => m.id === l.menuItemId);
        return {
          itemId: l.menuItemId,
          name: l.itemName,
          price: Number(l.unitPrice),
          quantity: l.quantity,
          categoryName: mi?.categoryName,
          taxRate: mi?.taxRate,
          taxType: mi?.taxType,
          kotBotType: mi?.kotBotType,
          imageUrl: mi?.imageUrl,
          discountAmount: Number(l.discountAmount),
        };
      }));
      setShowOpenOrders(false);
      setShowNewOrderModal(false);
      setLastOrder(null);
    } catch { /* non-fatal */ }
  };

  /* ── Confirm New Order modal ── */
  // No table-occupancy guard here: unsent (pending KOT/BOT) orders can't be
  // left behind — the New Order button is disabled until they're printed — so
  // starting another order on the same table is always intentional.
  const tableOptions = apiTables.length > 1 ? apiTables : FALLBACK_TABLE_OPTIONS;

  const startOrder = () => {
    setOrderType(modalOrderType);
    if (modalOrderType === 'Dine In') {
      setTableId(modalTableId);
      setTableNumber(modalTable);
      setNumberOfPax(modalPax);
    } else {
      setTableId(0);
      setTableNumber('');
      setNumberOfPax(1);
    }
    setShowNewOrderModal(false);
  };

  /* ── KOT delta tracking ── */
  // True when the cart has items or quantities the kitchen/bar has not seen yet.
  // Quantity decreases are ignored — those items are already being prepared.
  const hasUnsentChanges = orderItems.some(i => i.quantity > (sentQtys[i.itemId] ?? 0));
  const kotUpToDate = kotIssued && !hasUnsentChanges;

  /* ── Totals ── */
  const selectedOutlet    = outlets.find(o => o.id === outletId);
  const subtotal          = orderItems.reduce((s, o) => s + Math.max(0, o.price * o.quantity - o.discountAmount), 0);
  const taxAmt            = orderItems.reduce((s, o) => {
    if (!o.taxType || !o.taxRate) return s;
    return s + Math.max(0, o.price * o.quantity - o.discountAmount) * (o.taxRate / 100);
  }, 0);
  const serviceChargeAmt  = selectedOutlet?.serviceChargePercent
    ? subtotal * (selectedOutlet.serviceChargePercent / 100) : 0;
  const deliveryChargeAmt = orderType === 'Delivery' ? (selectedOutlet?.deliveryCharge ?? 0) : 0;
  const discVal           = parseFloat(discValue || '0') || 0;
  const orderDiscountAmt  = discType === 'percent'
    ? Math.min(subtotal * discVal / 100, subtotal)
    : Math.min(discVal, subtotal);
  const total             = Math.max(0, subtotal + taxAmt + serviceChargeAmt + deliveryChargeAmt - orderDiscountAmt);
  const itemCount         = orderItems.reduce((s, o) => s + o.quantity, 0);
  const totalPaid   = paymentEntries.reduce((s, e) => s + e.amount, 0);
  const remaining   = Math.max(0, total - totalPaid);
  const changeAmt   = Math.max(0, totalPaid - total);
  const isCash      = paymentTypes.find(t => t.name === selPayMethod)?.paymentCategory === 'Cash';

  const quickAmounts = useMemo(() => {
    const base = remaining > 0 ? remaining : total;
    if (base <= 0) return [];
    const steps = [500, 1000, 5000, 10000];
    const result = new Set<number>();
    for (const step of steps) {
      const v = Math.ceil(base / step) * step;
      if (v > base) result.add(v);
    }
    return Array.from(result).sort((a, b) => a - b).slice(0, 4);
  }, [remaining, total]);

  /* ── Build order lines payload ── */
  const buildLines = () => orderItems.map(i => ({
    menuItemId: i.itemId,
    itemName: i.name,
    unitPrice: i.price,
    quantity: i.quantity,
    discountAmount: i.discountAmount ?? 0,
  }));

  /* ── Payment entry helpers ── */
  const addPaymentEntry = () => {
    const amt = parseFloat(payEntryAmount || '0');
    if (!selPayMethod || amt <= 0) return;
    const cat = paymentTypes.find(t => t.name === selPayMethod)?.paymentCategory ?? '';
    setPaymentEntries(prev => [
      ...prev,
      { method: selPayMethod, category: cat, amount: parseFloat(amt.toFixed(2)), reference: payEntryRef.trim() || undefined },
    ]);
    const newRemaining = remaining - amt;
    setPayEntryAmount(newRemaining > 0 ? newRemaining.toFixed(2) : '');
    setPayEntryRef('');
  };

  const removePaymentEntry = (idx: number) =>
    setPaymentEntries(prev => prev.filter((_, i) => i !== idx));

  /* ── Payment method visual ── */
  const getPayMethodVisual = (name: string, category: string) => {
    const n = name.toLowerCase();
    if (n.includes('visa')) return (
      <div style={{ background: '#1a1f71', borderRadius: 3, padding: '2px 6px', color: '#fff', fontWeight: 900, fontSize: 10, letterSpacing: 1 }}>VISA</div>
    );
    if (n.includes('master')) return (
      <div style={{ position: 'relative', width: 30, height: 18, flexShrink: 0 }}>
        <div style={{ position: 'absolute', left: 0, width: 18, height: 18, borderRadius: '50%', background: '#eb001b' }} />
        <div style={{ position: 'absolute', left: 12, width: 18, height: 18, borderRadius: '50%', background: '#f79e1b', opacity: 0.85 }} />
      </div>
    );
    if (n.includes('amex') || n.includes('american express')) return (
      <div style={{ background: '#2E77BC', borderRadius: 3, padding: '2px 5px', color: '#fff', fontWeight: 900, fontSize: 9 }}>AMEX</div>
    );
    switch ((category ?? '').toLowerCase()) {
      case 'cash':    return <Banknote size={20} color="#16a34a" />;
      case 'card':    return <CreditCard size={20} color="#2563eb" />;
      case 'digital':
      case 'mobile':  return <Smartphone size={20} color="#7c3aed" />;
      case 'gift':    return <Gift size={20} color="#db2777" />;
      case 'loyalty': return <Star size={20} color="#ea580c" />;
      default:        return <Wallet size={20} color="#6b7280" />;
    }
  };

  const getPayMethodBg = (category: string) => {
    switch ((category ?? '').toLowerCase()) {
      case 'cash':    return '#f0fdf4';
      case 'card':    return '#eff6ff';
      case 'digital':
      case 'mobile':  return '#f5f3ff';
      case 'gift':    return '#fdf2f8';
      case 'loyalty': return '#fff7ed';
      default:        return '#f9fafb';
    }
  };

  /* ── Settlement ── */
  const openSettle = () => {
    if (orderItems.length === 0) return;
    setShowSettle(true);
    setPaymentEntries([]);
    setPayEntryAmount(total.toFixed(2));
    setPayEntryRef('');
    setSettleError(null);
  };

  const confirmSettle = async () => {
    if (paymentEntries.length === 0) {
      setSettleError('Please add at least one payment.');
      return;
    }
    if (remaining > 0.005) {
      setSettleError(`Unpaid balance: ${fmtNum.format(remaining)}. Add more payment to proceed.`);
      return;
    }
    const methodName = paymentEntries.length === 1
      ? paymentEntries[0].method
      : paymentEntries.map(e => e.method).join(' + ');
    setSaving(true);
    setSettleError(null);
    try {
      let order: RestaurantOrderDTO;

      if (activeOrderId !== null) {
        await restaurantOrderService.updateLines(activeOrderId, {
          lines: buildLines(),
          discountAmount: orderDiscountAmt,
        });
        const res = await restaurantOrderService.settle(activeOrderId, {
          paymentMethod: methodName,
          amountPaid: totalPaid,
          changeGiven: changeAmt,
        });
        order = (res.data as any)?.data ?? res.data;
      } else {
        const res = await restaurantOrderService.create({
          outletId: outletId !== '' ? outletId : undefined,
          orderType: orderType === 'Dine In' ? 'DineIn' : orderType,
          tableId: tableId > 0 ? tableId : undefined,
          tableNumber: tableNumber.trim() || undefined,
          numberOfPax: orderType === 'Dine In' ? numberOfPax : undefined,
          customerName: customerName.trim() || undefined,
          discountAmount: orderDiscountAmt,
          paymentMethod: methodName,
          amountPaid: totalPaid,
          changeGiven: changeAmt,
          lines: buildLines(),
        });
        order = (res.data as any)?.data ?? res.data;
      }

      setLastOrder(order);
      setShowSettle(false);
      setActiveOrderId(null);
      setKotIssued(false);
      setSentQtys({});
      setOrderItems([]);
      setDiscValue('');
      setDiscType('percent');
      setEditDiscId(null);
      setTableNumber('');
      setCustomerName('');
      setPaymentEntries([]);
    } catch (err: unknown) {
      const axErr = err as import('axios').AxiosError<{ error?: { message?: string } }>;
      setSettleError(axErr?.response?.data?.error?.message || 'Failed to settle order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /* ── KOT/BOT ticket print helper ── */
  const printKotTicket = (ticket: IssueKotResponseDTO) => {
    const rows = ticket.lines.map(l => `
      <tr>
        <td style="padding:5px 8px;font-weight:700;font-size:16px;width:36px;">${l.quantity}×</td>
        <td style="padding:5px 8px;font-size:15px;">${l.itemName}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><title>${ticket.ticketType}</title>
<style>body{font-family:'Courier New',monospace;margin:0;padding:12px;max-width:300px;}
.hdr{text-align:center;border-bottom:2px dashed #000;padding-bottom:8px;margin-bottom:8px;}
.badge{display:inline-block;background:#000;color:#fff;padding:3px 14px;font-size:20px;font-weight:900;letter-spacing:5px;}
.num{font-size:14px;font-weight:700;margin-top:4px;}
table{width:100%;border-collapse:collapse;}
.ftr{text-align:center;border-top:2px dashed #000;margin-top:8px;padding-top:6px;font-size:11px;}
@media print{body{margin:0;}}</style></head><body>
<div class="hdr">
  <div class="badge">${ticket.ticketType}</div>
  <div class="num">${ticket.ticketNumber}</div>
  ${ticket.orderNumber ? `<div style="font-size:11px;color:#555;">Order: ${ticket.orderNumber}</div>` : ''}
  <div style="margin-top:4px;font-size:12px;">${ticket.tableNumber ? 'Table: ' + ticket.tableNumber : ticket.orderType}${ticket.numberOfPax ? ' · ' + ticket.numberOfPax + ' Pax' : ''}</div>
  <div style="font-size:11px;">${new Date(ticket.printedAt).toLocaleString()}</div>
</div>
<table>${rows}</table>
<div class="ftr">— ${ticket.lines.length} item(s) —</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=320,height=500,menubar=no,toolbar=no');
    if (!win) { alert('Allow popups to print.'); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 350);
  };

  /* ── Void KOT print ── */
  const printVoidKotTicket = (ticket: IssueKotResponseDTO) => {
    const rows = ticket.lines.map(l => `
      <tr>
        <td style="padding:5px 8px;font-weight:700;font-size:16px;width:36px;text-decoration:line-through;">${l.quantity}×</td>
        <td style="padding:5px 8px;font-size:15px;text-decoration:line-through;">${l.itemName}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><title>VOID ${ticket.ticketType}</title>
<style>body{font-family:'Courier New',monospace;margin:0;padding:12px;max-width:300px;}
.hdr{text-align:center;border-bottom:2px dashed #000;padding-bottom:8px;margin-bottom:8px;}
.void-badge{display:inline-block;background:#dc2626;color:#fff;padding:3px 14px;font-size:20px;font-weight:900;letter-spacing:3px;}
.num{font-size:14px;font-weight:700;margin-top:4px;}
table{width:100%;border-collapse:collapse;}
.reason{background:#fff3cd;border:1px dashed #f59e0b;padding:6px 8px;font-size:12px;margin-top:6px;word-break:break-word;}
.ftr{text-align:center;border-top:2px dashed #000;margin-top:8px;padding-top:6px;font-size:11px;}
@media print{body{margin:0;}}</style></head><body>
<div class="hdr">
  <div class="void-badge">VOID ${ticket.ticketType}</div>
  <div class="num">${ticket.ticketNumber}</div>
  ${ticket.orderNumber ? `<div style="font-size:11px;color:#555;">Order: ${ticket.orderNumber}</div>` : ''}
  <div style="margin-top:4px;font-size:12px;">${ticket.tableNumber ? 'Table: ' + ticket.tableNumber : ticket.orderType}${ticket.numberOfPax ? ' · ' + ticket.numberOfPax + ' Pax' : ''}</div>
  <div style="font-size:11px;">${new Date(ticket.printedAt).toLocaleString()}</div>
</div>
<table>${rows}</table>
<div class="reason"><strong>Void Reason:</strong><br>${ticket.voidReason ?? ''}</div>
<div class="ftr">— VOID TICKET — ${ticket.lines.length} item(s) —</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=320,height=540,menubar=no,toolbar=no');
    if (!win) { alert('Allow popups to print.'); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 350);
  };

  /* ── Confirm void KOT items ── */
  const confirmVoidKotItem = async () => {
    if (!voidModalItem || !voidReason.trim() || !activeOrderId) return;
    setVoiding(true);
    try {
      const item: KotTicketLineDTO = {
        menuItemId: voidModalItem.itemId,
        itemName: voidModalItem.name,
        quantity: sentQtys[voidModalItem.itemId] ?? voidModalItem.quantity,
      };
      const res = await restaurantOrderService.voidKotItems(activeOrderId, { reason: voidReason.trim(), items: [item] });
      const data = (res.data as any)?.data ?? res.data;
      (data.voidTickets as IssueKotResponseDTO[]).forEach(printVoidKotTicket);

      // Remove the item from the current order view and clear its sentQty
      setOrderItems(prev => prev.filter(o => o.itemId !== voidModalItem.itemId));
      setSentQtys(prev => { const n = { ...prev }; delete n[voidModalItem.itemId]; return n; });
      setVoidModalItem(null);
      setVoidReason('');
    } catch (err: unknown) {
      const axErr = err as import('axios').AxiosError<any>;
      const status = axErr?.response?.status;
      const body   = axErr?.response?.data;
      const msg    = body?.error?.message ?? body?.message ?? body?.title ?? JSON.stringify(body);
      alert(`Void Error [${status ?? 'network'}]: ${msg || err}`);
    } finally {
      setVoiding(false);
    }
  };

  /* ── Issue KOT / BOT (calls backend, keeps bill open) ── */
  const printKotBot = async () => {
    if (orderItems.length === 0) return;
    setSaving(true);
    try {
      let orderId = activeOrderId;

      if (orderId === null) {
        // Create the order as Open (no payment) and persist it
        const res = await restaurantOrderService.create({
          outletId: outletId !== '' ? outletId : undefined,
          orderType: orderType === 'Dine In' ? 'DineIn' : orderType,
          tableId: tableId > 0 ? tableId : undefined,
          tableNumber: tableNumber.trim() || undefined,
          numberOfPax: orderType === 'Dine In' ? numberOfPax : undefined,
          customerName: customerName.trim() || undefined,
          discountAmount: orderDiscountAmt,
          lines: buildLines(),
        });
        const newOrder: RestaurantOrderDTO = (res.data as any)?.data ?? res.data;
        orderId = newOrder.id;
        setActiveOrderId(orderId);
      } else {
        // Sync current items to existing order
        await restaurantOrderService.updateLines(orderId, {
          lines: buildLines(),
          discountAmount: orderDiscountAmt,
        });
      }

      // Delta ticket: send only items/quantities the kitchen/bar hasn't seen.
      // A re-print after adding 2 more of an item produces a ticket for just
      // those 2 — never a duplicate of the full order.
      const deltaItems = orderItems
        .map(i => ({ ...i, deltaQty: i.quantity - (sentQtys[i.itemId] ?? 0) }))
        .filter(i => i.deltaQty > 0);

      const kotItems = deltaItems.filter(i => i.kotBotType !== 'BOT');
      const botItems = deltaItems.filter(i => i.kotBotType === 'BOT' || i.kotBotType === 'Both');

      if (kotItems.length > 0) {
        const kotRes = await restaurantOrderService.issueKot(orderId, {
          ticketType: 'KOT',
          lines: kotItems.map(i => ({ menuItemId: i.itemId, itemName: i.name, quantity: i.deltaQty })),
        });
        const kotData: IssueKotResponseDTO = (kotRes.data as any)?.data ?? kotRes.data;
        printKotTicket(kotData);
      }

      if (botItems.length > 0) {
        const botRes = await restaurantOrderService.issueKot(orderId, {
          ticketType: 'BOT',
          lines: botItems.map(i => ({ menuItemId: i.itemId, itemName: i.name, quantity: i.deltaQty })),
        });
        const botData: IssueKotResponseDTO = (botRes.data as any)?.data ?? botRes.data;
        printKotTicket(botData);
      }

      // Tickets issued — the kitchen/bar now has every current item, payment
      // may proceed. Snapshot the sent quantities for the next delta.
      setSentQtys(Object.fromEntries(orderItems.map(i => [i.itemId, i.quantity])));
      setKotIssued(true);
    } catch (err: unknown) {
      const axErr = err as import('axios').AxiosError<any>;
      const status  = axErr?.response?.status;
      const body    = axErr?.response?.data;
      const msg     = body?.error?.message ?? body?.message ?? body?.title ?? JSON.stringify(body);
      alert(`KOT/BOT Error [${status ?? 'network'}]: ${msg || err}`);
    } finally {
      setSaving(false);
    }
  };

  /* ── Shared receipt HTML builder ── */
  const buildReceiptHtml = (opts: {
    title: string;
    orderNumber?: string;
    tableNum: string;
    orderTypeLabel: string;
    customerNameVal: string;
    date: Date;
    lines: { code?: string; name: string; qty: number; unitPrice: number; lineTotal: number }[];
    subTotal: number;
    discountAmount: number;
    taxAmount: number;
    serviceChargeAmount: number;
    deliveryChargeAmount: number;
    total: number;
    paymentMethod?: string;
    amountPaid?: number;
    changeGiven?: number;
  }): string => {
    const s = companySettings;
    const is80mm = s?.billPaperSize === BillPaperSize.POS_80;
    const maxW   = is80mm ? '80mm' : '210mm';
    const twoLine = s?.billItemLineFormat === BillItemLineFormat.TWO_LINE;

    const cols = 1
      + (s?.billShowQty       !== false ? 1 : 0)
      + (s?.billShowUnitPrice !== false ? 1 : 0)
      + (s?.billShowLineTotal !== false ? 1 : 0)
      + (s?.billShowItemCode  ? 1 : 0);

    const optRow = (label: string, val: number, neg = false) =>
      val > 0
        ? `<tr><td colspan="${cols - 1}" style="text-align:right;padding:2px 4px;">${label}</td>
             <td style="text-align:right;padding:2px 4px;">${neg ? '-' : ''}${fmtNum.format(val)}</td></tr>`
        : '';

    const lineRows = opts.lines.map(l => {
      if (twoLine) {
        return `<tr>
          <td colspan="${cols}" style="padding:3px 4px;">${l.name}${s?.billShowItemCode && l.code ? ` <span style="color:#666">[${l.code}]</span>` : ''}</td>
        </tr><tr>
          ${s?.billShowQty       !== false ? `<td style="padding:1px 4px 4px;color:#555;">${l.qty} ×</td>` : ''}
          ${s?.billShowUnitPrice !== false ? `<td style="padding:1px 4px 4px;text-align:right;color:#555;">${fmtNum.format(l.unitPrice)}</td>` : ''}
          ${s?.billShowLineTotal !== false ? `<td style="padding:1px 4px 4px;text-align:right;font-weight:600;">${fmtNum.format(l.lineTotal)}</td>` : ''}
        </tr>`;
      }
      return `<tr>
        ${s?.billShowItemCode  ? `<td style="padding:3px 4px;font-size:10px;color:#666;">${l.code ?? ''}</td>` : ''}
        <td style="padding:3px 4px;">${l.name}</td>
        ${s?.billShowQty       !== false ? `<td style="padding:3px 4px;text-align:center;">${l.qty}</td>` : ''}
        ${s?.billShowUnitPrice !== false ? `<td style="padding:3px 4px;text-align:right;">${fmtNum.format(l.unitPrice)}</td>` : ''}
        ${s?.billShowLineTotal !== false ? `<td style="padding:3px 4px;text-align:right;">${fmtNum.format(l.lineTotal)}</td>` : ''}
      </tr>`;
    }).join('');

    const thCols = [
      s?.billShowItemCode  ? `<th style="text-align:left;">Code</th>` : '',
      `<th style="text-align:left;">Item</th>`,
      s?.billShowQty       !== false ? `<th style="text-align:center;">Qty</th>`   : '',
      s?.billShowUnitPrice !== false ? `<th style="text-align:right;">Price</th>`  : '',
      s?.billShowLineTotal !== false ? `<th style="text-align:right;">Total</th>`  : '',
    ].join('');

    const logoHtml  = s?.billShowLogo && s?.logoUrl
      ? `<img src="${s.logoUrl}" style="max-height:60px;max-width:180px;margin-bottom:4px;" /><br>` : '';
    const nameHtml  = s?.billShowCompanyName && s?.companyName
      ? `<div style="font-weight:700;font-size:14px;">${s.companyName}</div>` : '';
    const addrHtml  = s?.billShowAddress && s?.addressLine1
      ? `<div style="font-size:11px;">${s.addressLine1}${s.addressLine2 ? ', ' + s.addressLine2 : ''}${s.city ? ', ' + s.city : ''}</div>` : '';
    const phoneHtml = s?.billShowPhone && s?.phone
      ? `<div style="font-size:11px;">Tel: ${s.phone}</div>` : '';
    const emailHtml = s?.billShowEmail && s?.email
      ? `<div style="font-size:11px;">${s.email}</div>` : '';
    const webHtml   = s?.billShowWebsite && s?.website
      ? `<div style="font-size:11px;">${s.website}</div>` : '';
    const hdr1Html  = s?.billHeaderLine1
      ? `<div style="font-size:11px;margin-top:3px;">${s.billHeaderLine1}</div>` : '';
    const hdr2Html  = s?.billHeaderLine2
      ? `<div style="font-size:11px;">${s.billHeaderLine2}</div>` : '';
    const ftr1Html  = s?.billFooterLine1 ? `<div>${s.billFooterLine1}</div>` : '';
    const ftr2Html  = s?.billFooterLine2 ? `<div>${s.billFooterLine2}</div>` : '';
    const defaultFooter = (!s?.billFooterLine1 && !s?.billFooterLine2) ? `<div>Thank you! Visit Again</div>` : '';

    const payHtml = s?.billShowPaymentMethod !== false && opts.paymentMethod
      ? `<div style="font-size:12px;">
           Payment: ${opts.paymentMethod}<br>
           ${(opts.amountPaid ?? 0) > 0 ? 'Tendered: ' + fmtNum.format(opts.amountPaid!) + '<br>' : ''}
           ${(opts.changeGiven ?? 0) > 0 ? 'Change: ' + fmtNum.format(opts.changeGiven!) : ''}
         </div><div class="div"></div>` : '';

    return `<!DOCTYPE html><html><head><title>${opts.title}</title>
<style>
  body{font-family:'Courier New',monospace;margin:0;padding:12px;max-width:${maxW};font-size:13px;}
  .center{text-align:center;}
  .div{border-top:1px dashed #000;margin:6px 0;}
  table{width:100%;border-collapse:collapse;}
  th{font-size:10px;font-weight:700;text-transform:uppercase;border-bottom:1px solid #000;padding:4px;}
  .tot{font-weight:700;font-size:14px;border-top:1px solid #000;}
  @media print{body{margin:0;}}
</style></head><body>
<div class="center" style="margin-bottom:6px;">
  ${logoHtml}${nameHtml}${addrHtml}${phoneHtml}${emailHtml}${webHtml}${hdr1Html}${hdr2Html}
  <div style="font-size:17px;font-weight:900;margin-top:6px;">${opts.title}</div>
  ${opts.orderNumber ? `<div style="font-size:12px;">${opts.orderNumber}</div>` : ''}
</div>
<div class="div"></div>
<div style="font-size:11px;margin-bottom:5px;">
  ${opts.tableNum ? 'Table: ' + opts.tableNum + ' &middot; ' : ''}${opts.orderTypeLabel}
  &middot; ${opts.date.toLocaleString()}
  ${opts.customerNameVal ? '<br>Customer: ' + opts.customerNameVal : ''}
</div>
<table>
  <thead><tr>${thCols}</tr></thead>
  <tbody>${lineRows}</tbody>
</table>
<div class="div"></div>
<table>
  <tr><td colspan="${cols - 1}" style="text-align:right;padding:2px 4px;">Sub Total</td>
      <td style="text-align:right;padding:2px 4px;">${fmtNum.format(opts.subTotal)}</td></tr>
  ${optRow('Discount', opts.discountAmount, true)}
  ${optRow('Tax', opts.taxAmount)}
  ${optRow('Service Charge', opts.serviceChargeAmount)}
  ${optRow('Delivery', opts.deliveryChargeAmount)}
  <tr class="tot">
    <td colspan="${cols - 1}" style="text-align:right;padding:4px;">TOTAL</td>
    <td style="text-align:right;padding:4px;">${fmtNum.format(opts.total)}</td>
  </tr>
</table>
<div class="div"></div>
${payHtml}
<div class="center" style="font-size:11px;margin-top:8px;">
  ${ftr1Html}${ftr2Html}${defaultFooter}
</div>
</body></html>`;
  };

  const openPrintWindow = (html: string, is80mm: boolean) => {
    const w = is80mm ? 340 : 820;
    const h = is80mm ? 680 : 1000;
    const win = window.open('', '_blank', `width=${w},height=${h},menubar=no,toolbar=no`);
    if (!win) { alert('Allow popups to print.'); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 350);
  };

  const printBill = (order: RestaurantOrderDTO) => {
    const html = buildReceiptHtml({
      title: 'RECEIPT',
      orderNumber: order.orderNumber,
      tableNum: order.tableNumber ?? '',
      orderTypeLabel: order.orderType ?? '',
      customerNameVal: order.customerName ?? '',
      date: new Date(order.createdAt),
      lines: order.lines.map(l => ({
        code: (l as any).menuCode,
        name: l.itemName,
        qty: l.quantity,
        unitPrice: Number(l.unitPrice),
        lineTotal: Number(l.lineTotal),
      })),
      subTotal: Number(order.subTotal),
      discountAmount: Number(order.discountAmount),
      taxAmount: Number(order.taxAmount),
      serviceChargeAmount: Number(order.serviceChargeAmount),
      deliveryChargeAmount: Number(order.deliveryChargeAmount),
      total: Number(order.totalAmount),
      paymentMethod: order.paymentMethod ?? undefined,
      amountPaid: Number(order.amountPaid),
      changeGiven: Number(order.changeGiven),
    });
    openPrintWindow(html, companySettings?.billPaperSize === BillPaperSize.POS_80);
  };

  const printPreBill = () => {
    if (orderItems.length === 0) return;
    const html = buildReceiptHtml({
      title: 'PRE-BILL',
      tableNum: tableNumber,
      orderTypeLabel: orderType,
      customerNameVal: customerName,
      date: new Date(),
      lines: orderItems.map(i => {
        const lineNet = Math.max(0, i.price * i.quantity - i.discountAmount);
        return { name: i.name, qty: i.quantity, unitPrice: i.price, lineTotal: lineNet };
      }),
      subTotal: subtotal,
      discountAmount: orderDiscountAmt,
      taxAmount: taxAmt,
      serviceChargeAmount: serviceChargeAmt,
      deliveryChargeAmount: deliveryChargeAmt,
      total,
    });
    openPrintWindow(html, companySettings?.billPaperSize === BillPaperSize.POS_80);
  };

  /* ── Page range for pagination ── */
  const pageRange = useMemo(() => {
    const max = 5;
    if (totalPages <= max) return Array.from({ length: totalPages }, (_, i) => i);
    const half = Math.floor(max / 2);
    let start = Math.max(0, menuPage - half);
    const end = Math.min(totalPages - 1, start + max - 1);
    start = Math.max(0, end - max + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [totalPages, menuPage]);

  /* ── Render ── */
  const pageContent = (
    <>
      <style>{`main { padding: 5px !important; }`}</style>

      <div className="pos-page">


        {/* ════════════════════════════════════════════════════════
            MENU PANEL
            ════════════════════════════════════════════════════════ */}
        <div className="pos-menu-panel">

          <div className="pos-search-bar">
            <Search size={16} className="pos-search-icon" />
            <input
              className="pos-search-input"
              placeholder="Search menu items…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="pos-search-clear" onClick={() => setSearch('')}>
                <X size={14} />
              </button>
            )}
          </div>

          <p className="pos-section-label">Categories</p>
          <div className="pos-category-tabs">
            <button
              className={`pos-cat-tab${selCatId === null ? ' active' : ''}`}
              onClick={() => handleCategory(null)}
            >
              <span className="pos-cat-icon"><UtensilsCrossed size={18} /></span>
              <span className="pos-cat-name">All</span>
            </button>
            {categories.map((cat, idx) => {
              const Icon = CAT_ICONS[idx % CAT_ICONS.length];
              return (
                <button
                  key={cat.id}
                  className={`pos-cat-tab${selCatId === cat.id ? ' active' : ''}`}
                  onClick={() => handleCategory(cat.id)}
                >
                  <span className="pos-cat-icon"><Icon size={18} /></span>
                  <span className="pos-cat-name">{cat.name}</span>
                </button>
              );
            })}
          </div>

          <div className="pos-menu-heading">
            <h3 className="pos-menu-heading-title">
              {selCatId ? (categories.find(c => c.id === selCatId)?.name ?? 'Menu') : 'All Menu Items'}
              {sortedItems.length > 0 && <span className="pos-item-count">{sortedItems.length}</span>}
            </h3>
            <div className="pos-sort-ctrl">
              <ArrowUpDown size={13} />
              <span>Sort by</span>
              <select className="pos-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value as SortBy)}>
                <option value="popular">Popular</option>
                <option value="name">Name A–Z</option>
                <option value="price-asc">Price ↑</option>
                <option value="price-desc">Price ↓</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="pos-placeholder">Loading menu…</div>
          ) : sortedItems.length === 0 ? (
            <div className="pos-placeholder">No items found</div>
          ) : (
            <div className="pos-menu-grid">
              {paginatedItems.map(item => {
                const localQty = getLocalQty(item.id);
                const hhEligible = isHappyHourEligible(item.id, item.menuCategoryId);
                const hhDiscount = activeHappyHour && hhEligible
                  ? (activeHappyHour.discountType === 'Percentage'
                      ? `${activeHappyHour.discountValue}% off`
                      : `${fmtNum.format(Number(activeHappyHour.discountValue))} off`)
                  : null;
                const hhPrice = hhEligible && activeHappyHour
                  ? (activeHappyHour.discountType === 'Percentage'
                      ? item.price * (1 - activeHappyHour.discountValue / 100)
                      : Math.max(0, item.price - activeHappyHour.discountValue))
                  : null;
                return (
                      <div key={item.id} className={`pos-menu-card${!item.isAvailable ? ' pos-card-unavailable' : ''}`}>
                        <div className="pos-card-img" style={{ position: 'relative' }}>
                          {item.typeName && <span className="pos-card-badge">{item.typeName}</span>}
                          {item.imageUrl ? (
                            <img className="pos-card-img-photo" src={item.imageUrl} alt={item.name} />
                          ) : (
                            <div className="pos-card-img-inner"><ChefHat size={40} strokeWidth={1.2} /></div>
                          )}
                          {!item.isAvailable && <div className="pos-card-unavail-overlay">Unavailable</div>}
                          {hhDiscount && (
                            <span style={{
                              position: 'absolute', bottom: 6, left: 6,
                              background: 'linear-gradient(90deg,#f59e0b,#d97706)',
                              color: '#fff', padding: '2px 8px', borderRadius: 99,
                              fontSize: 10, fontWeight: 700,
                              display: 'flex', alignItems: 'center', gap: 3,
                              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                              lineHeight: 1.4,
                            }}>
                              🎉 {hhDiscount}
                            </span>
                          )}
                        </div>
                        <div className="pos-card-body">
                          <div className="pos-card-name">{item.name}</div>
                          <div className="pos-card-desc">{item.description || item.categoryName || ''}</div>
                          <div className="pos-card-price-row">
                            {hhPrice !== null ? (
                              <span style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                <span style={{ textDecoration: 'line-through', color: 'var(--ims-text-sub)', fontSize: 10, lineHeight: 1.2 }}>
                                  {fmtNum.format(Number(item.price))}
                                </span>
                                <span className="pos-card-price" style={{ color: '#16a34a', lineHeight: 1.2 }}>
                                  {fmtNum.format(hhPrice)}
                                </span>
                              </span>
                            ) : (
                              <span className="pos-card-price">{fmtNum.format(Number(item.price))}</span>
                            )}
                            <div className="pos-card-qty-ctrl">
                              <button className="pos-card-qty-btn" onClick={e => { e.stopPropagation(); changeLocalQty(item.id, -1); }}>−</button>
                              <span className="pos-card-qty-val">{localQty}</span>
                              <button className="pos-card-qty-btn" onClick={e => { e.stopPropagation(); changeLocalQty(item.id, 1); }}>+</button>
                            </div>
                          </div>
                        </div>
                        <button className="pos-add-btn" onClick={() => addItem(item)} disabled={!item.isAvailable}>
                          <Plus size={13} strokeWidth={2.5} /> Add to Order
                        </button>
                      </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="pos-pagination">
              <button className="pos-page-btn" disabled={menuPage === 0} onClick={() => setMenuPage(p => p - 1)}>
                <ChevronLeft size={16} />
              </button>
              {pageRange.map(p => (
                <button key={p} className={`pos-page-btn${menuPage === p ? ' active' : ''}`} onClick={() => setMenuPage(p)}>
                  {p + 1}
                </button>
              ))}
              <button className="pos-page-btn" disabled={menuPage >= totalPages - 1} onClick={() => setMenuPage(p => p + 1)}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════
            ORDER PANEL
            ════════════════════════════════════════════════════════ */}
        <div className="pos-order-panel">

          {/* Header */}
          <div className="pos-order-header">
            <div className="pos-order-header-left">
              <ShoppingCart size={17} />
              <h2 className="pos-order-title">
                {lastOrder
                  ? lastOrder.orderNumber
                  : activeOrderId
                    ? `Order #${activeOrderId}`
                    : 'New Order'}
              </h2>
            </div>
            <div className="pos-order-header-right">
              <button
                className="pos-header-icon-btn"
                title="Open Orders"
                onClick={() => { setShowOpenOrders(true); loadOpenOrders(); }}
              >
                <History size={15} />
              </button>
              {(orderItems.length > 0 || lastOrder) && (
                <button
                  className="pos-clear-btn"
                  title="Clear order"
                  onClick={() => {
                    // Receipt view (lastOrder) is already settled — no confirm needed.
                    if (orderItems.length === 0 || window.confirm('Discard the current order items?')) clearOrder();
                  }}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Order type tabs */}
          {/* Display-only: the type is chosen on the new-order screen and
              cannot change mid-order (charges/table rules differ per type). */}
          <div className="pos-order-type-tabs">
            {ORDER_TYPES.map(type => (
              <span
                key={type}
                className={`pos-type-tab pos-type-tab-static${orderType === type ? ' active' : ''}`}
              >
                {type}
              </span>
            ))}
          </div>

          {/* Active order info badge */}
          {!lastOrder && !showNewOrderModal && (orderType === 'Dine In' || activeOrderId) && (
            <div className="pos-order-info-bar">
              {orderType === 'Dine In' && tableNumber && (
                <span className="pos-order-info-chip">
                  <MapPin size={11} /> {tableNumber}
                </span>
              )}
              {orderType === 'Dine In' && numberOfPax > 0 && (
                <span className="pos-order-info-chip">
                  <Users size={11} /> {numberOfPax} Pax
                </span>
              )}
              {activeOrderId && (
                <span className="pos-order-info-chip pos-order-info-chip--active">
                  <Check size={11} /> Saved
                </span>
              )}
            </div>
          )}

          {/* ── Success state ── */}
          {lastOrder ? (
            <div className="pos-success-state">
              <div className="pos-success-icon"><Check size={26} strokeWidth={2.5} /></div>
              <p className="pos-success-title">Order Settled!</p>
              <p className="pos-success-sub">{lastOrder.orderNumber}</p>
              {lastOrder.billNumber && (
                <p className="pos-success-sub" style={{ marginTop: 0, fontWeight: 700, color: '#16a34a' }}>
                  Bill: {lastOrder.billNumber}
                </p>
              )}
              {lastOrder.paymentMethod && (
                <p className="pos-success-sub" style={{ marginTop: 0 }}>
                  {lastOrder.paymentMethod} · {fmt.format(Number(lastOrder.amountPaid))}
                  {Number(lastOrder.changeGiven) > 0 && ` · Change: ${fmt.format(Number(lastOrder.changeGiven))}`}
                </p>
              )}
              <div className="pos-success-actions">
                <button className="pos-btn-charge" onClick={() => printBill(lastOrder)}>
                  <Printer size={15} /> Print Bill
                </button>
                <button className="pos-btn-charge" style={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)' }} onClick={clearOrder}>
                  <Plus size={15} /> New Order
                </button>
                <button className="ims-btn ims-btn-secondary" style={{ fontSize: '12px', padding: '4px 12px', marginTop: '0.25rem' }} onClick={() => navigate('/restaurant/orders')}>
                  View Order History
                </button>
              </div>
            </div>
          ) : showNewOrderModal ? (
            /* ── New Order initiation UI (inline in order panel) ── */
            <div className="pos-new-order-init">
              <div className="pos-new-order-init-icon">
                <svg viewBox="0 0 96 96" width="96" height="96" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="pos-ord-bg" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#fb923c" />
                      <stop offset="60%" stopColor="#ea580c" />
                      <stop offset="100%" stopColor="#9a3412" />
                    </linearGradient>
                    <radialGradient id="pos-ord-shine" cx="35%" cy="30%" r="65%">
                      <stop offset="0%" stopColor="white" stopOpacity="0.22" />
                      <stop offset="100%" stopColor="white" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  {/* Drop shadow */}
                  <circle cx="48" cy="52" r="38" fill="#9a3412" opacity="0.18" />
                  {/* Main gradient circle */}
                  <circle cx="48" cy="48" r="40" fill="url(#pos-ord-bg)" />
                  {/* Shine overlay */}
                  <circle cx="48" cy="48" r="40" fill="url(#pos-ord-shine)" />
                  {/* Decorative plate rings */}
                  <circle cx="48" cy="55" r="22" fill="none" stroke="white" strokeWidth="1.8" opacity="0.3" />
                  <circle cx="48" cy="55" r="15" fill="white" opacity="0.12" />
                  {/* Fork tines */}
                  <rect x="28" y="29" width="2.8" height="10" rx="1.4" fill="white" />
                  <rect x="32.6" y="29" width="2.8" height="10" rx="1.4" fill="white" />
                  <rect x="37.2" y="29" width="2.8" height="10" rx="1.4" fill="white" />
                  {/* Fork neck */}
                  <path d="M28 39 Q33 41.5 40 39 L39.5 43 Q33 44.5 28.5 43 Z" fill="white" />
                  {/* Fork handle */}
                  <rect x="31.5" y="43" width="5" height="19" rx="2.5" fill="white" />
                  {/* Knife blade */}
                  <path d="M55 29 Q62 36 62 46 L55 46 Z" fill="white" />
                  {/* Knife handle */}
                  <rect x="55.5" y="46" width="5" height="16" rx="2.5" fill="white" />
                  {/* Steam wisps */}
                  <path d="M39 26 Q37 21 39.5 16" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5" />
                  <path d="M48 24 Q46 19 48.5 14" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5" />
                  <path d="M57 26 Q55 21 57.5 16" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5" />
                  {/* Sparkle dots */}
                  <circle cx="78" cy="22" r="3" fill="#fdba74" opacity="0.9" />
                  <circle cx="84" cy="34" r="2" fill="#fed7aa" opacity="0.7" />
                  <circle cx="14" cy="28" r="2" fill="#fed7aa" opacity="0.7" />
                  <circle cx="10" cy="42" r="3" fill="#fdba74" opacity="0.9" />
                </svg>
              </div>
              <p className="pos-new-order-init-title">Start a New Order</p>
              <p className="pos-new-order-init-sub">Select order type and table to begin</p>

              {/* Order type */}
              <div className="pos-new-order-type-grid">
                {ORDER_TYPES.map(type => (
                  <button
                    key={type}
                    className={`pos-new-order-type-btn${modalOrderType === type ? ' active' : ''}`}
                    onClick={() => setModalOrderType(type)}
                  >
                    {type === 'Dine In' ? <Utensils size={20} /> : type === 'Takeout' ? <ShoppingBag size={20} /> : <MapPin size={20} />}
                    <span>{type}</span>
                  </button>
                ))}
              </div>

              {/* Table + Pax for Dine In */}
              {modalOrderType === 'Dine In' && (
                <div className="pos-new-order-dinein">
                  <div className="pos-new-order-field">
                    <label className="pos-new-order-label"><MapPin size={13} /> Table</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select
                        className="pos-table-sel"
                        style={{ flex: 1 }}
                        value={modalTableId}
                        onChange={e => {
                          const opt = tableOptions.find(o => o.id === Number(e.target.value));
                          setModalTableId(opt?.id ?? 0);
                          setModalTable(opt?.value ?? '');
                          const raw = rawTables.find((t: any) => t.id === Number(e.target.value));
                          if (raw?.capacity) setModalPax(raw.capacity);
                        }}
                      >
                        {tableOptions.filter(o => o.value !== '').map(o => (
                          <option key={o.id || o.value} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowFloorPlan(true)}
                        title="Pick from floor plan"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '6px 10px', borderRadius: 7, border: '1px solid var(--ims-border)',
                          background: 'var(--ims-bg-card)', color: 'var(--ims-text-sub)',
                          cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        <LayoutGrid size={13} /> Floor Plan
                      </button>
                    </div>
                  </div>
                  <div className="pos-new-order-field">
                    <label className="pos-new-order-label"><Users size={13} /> Number of Pax</label>
                    <div className="pos-pax-grid">
                      {PAX_OPTIONS.map(n => (
                        <button
                          key={n}
                          className={`pos-pax-btn${modalPax === n ? ' active' : ''}`}
                          onClick={() => setModalPax(n)}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <button className="pos-btn-charge" style={{ marginTop: '1rem', width: '100%' }} onClick={startOrder}>
                <Plus size={16} /> Start Order
              </button>
            </div>
          ) : (
            <>
              {/* Items list */}
              <div className="pos-order-items">
                {orderItems.length === 0 ? (
                  <div className="pos-order-empty">
                    <ShoppingCart size={44} strokeWidth={1.2} />
                    <p>No items added</p>
                    <span>Select items from the menu</span>
                  </div>
                ) : (
                  orderItems.map(item => {
                    const lineGross = item.price * item.quantity;
                    const lineNet   = Math.max(0, lineGross - item.discountAmount);
                    const sentQty   = sentQtys[item.itemId] ?? 0;
                    const isLocked  = sentQty > 0;
                    return (
                      <div key={item.itemId} className={`pos-order-item${isLocked ? ' pos-order-item--locked' : ''}`}>
                        <div className="pos-oi-thumb">
                          {item.imageUrl ? <img src={item.imageUrl} alt={item.name} /> : <ChefHat size={18} strokeWidth={1.2} />}
                        </div>
                        <div className="pos-oi-main">
                          <div className="pos-oi-info">
                            <div className="pos-oi-name">
                              {item.name}
                              {isLocked && <Lock size={10} style={{ marginInlineStart: 4, color: '#dc2626', flexShrink: 0 }} />}
                            </div>
                            <div className="pos-oi-price">
                              {fmt.format(lineNet)}
                              {item.discountAmount > 0 && (
                                <span className="pos-oi-price-original">{fmt.format(lineGross)}</span>
                              )}
                            </div>
                            {editDiscId === item.itemId ? (
                              <div className="pos-item-discount">
                                <span className="pos-item-discount-label">Disc:</span>
                                <input
                                  className="pos-item-discount-input"
                                  type="number"
                                  min="0"
                                  value={discInput}
                                  onChange={e => setDiscInput(e.target.value)}
                                  onBlur={() => applyItemDiscount(item.itemId)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') applyItemDiscount(item.itemId);
                                    if (e.key === 'Escape') { setEditDiscId(null); setDiscInput(''); }
                                  }}
                                  autoFocus
                                />
                              </div>
                            ) : item.discountAmount > 0 ? (
                              <div
                                className="pos-item-discount"
                                onClick={() => { setEditDiscId(item.itemId); setDiscInput(item.discountAmount.toString()); }}
                              >
                                <span className="pos-item-discount-label">−{fmt.format(item.discountAmount)} disc</span>
                              </div>
                            ) : null}
                          </div>
                          <div className="pos-oi-controls">
                            {isLocked && canVoidKot ? (
                              <button
                                className="pos-qty-btn pos-void-btn"
                                title="Void sent item"
                                onClick={() => {
  setVoidModalItem(item);
  setVoidReason('');
  kotVoidReasonService.getAll()
    .then(res => {
      const d = (res.data as any)?.data ?? [];
      setKotVoidReasons(Array.isArray(d) ? d.filter((r: KotVoidReasonDTO) => r.isActive) : []);
    })
    .catch(() => setKotVoidReasons([]));
}}
                              >
                                <X size={11} strokeWidth={2.5} />
                              </button>
                            ) : (
                              <button
                                className="pos-qty-btn pos-disc-btn"
                                title="Item discount"
                                onClick={() => { setEditDiscId(item.itemId); setDiscInput(item.discountAmount > 0 ? item.discountAmount.toString() : ''); }}
                              >
                                <Tag size={9} />
                              </button>
                            )}
                            <button
                              className="pos-qty-btn"
                              disabled={item.quantity <= sentQty}
                              onClick={() => changeQty(item.itemId, -1)}
                            ><Minus size={11} strokeWidth={2.5} /></button>
                            <span className="pos-qty-val">{item.quantity}</span>
                            <button className="pos-qty-btn" onClick={() => changeQty(item.itemId, 1)}><Plus size={11} strokeWidth={2.5} /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Table / Customer */}
              <div className="pos-table-row">
                <select
                  className="pos-table-sel"
                  value={tableId}
                  onChange={e => {
                    const opt = tableOptions.find(o => o.id === Number(e.target.value));
                    setTableId(opt?.id ?? 0);
                    setTableNumber(opt?.value ?? '');
                  }}
                >
                  {tableOptions.map(o => (
                    <option key={o.id || o.value} value={o.id}>{o.label}</option>
                  ))}
                </select>
                <input
                  className="pos-customer-input"
                  placeholder="Walk-in Customer"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                />
              </div>

              {/* Summary */}
              <div className="pos-summary">
                <div className="pos-summary-row">
                  <span>Sub Total</span><span>{fmt.format(subtotal)}</span>
                </div>
                <div className="pos-summary-row">
                  <div className="pos-discount-ctrl">
                    <span>Discount</span>
                    <div className="pos-discount-toggle">
                      <button className={discType === 'percent' ? 'active' : ''} onClick={() => setDiscType('percent')}>%</button>
                      <button className={discType === 'flat' ? 'active' : ''} onClick={() => setDiscType('flat')}>#</button>
                    </div>
                    <input
                      className="pos-discount-num"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={discValue}
                      onChange={e => setDiscValue(e.target.value)}
                    />
                  </div>
                  <span style={{ color: '#dc2626' }}>
                    {orderDiscountAmt > 0 ? `−${fmt.format(orderDiscountAmt)}` : fmt.format(0)}
                  </span>
                </div>
                {taxAmt > 0 && (
                  <div className="pos-summary-row"><span>Tax</span><span>{fmt.format(taxAmt)}</span></div>
                )}
                {serviceChargeAmt > 0 && (
                  <div className="pos-summary-row">
                    <span>Service Charge ({selectedOutlet?.serviceChargePercent}%)</span>
                    <span>{fmt.format(serviceChargeAmt)}</span>
                  </div>
                )}
                {orderType === 'Delivery' && deliveryChargeAmt > 0 && (
                  <div className="pos-summary-row">
                    <span>Delivery Charge</span><span>{fmt.format(deliveryChargeAmt)}</span>
                  </div>
                )}
              </div>

              {/* Grand Total */}
              <div className="pos-grand-total">
                <span className="pos-grand-label">Grand Total</span>
                <span className="pos-grand-amount">{fmt.format(total)}</span>
              </div>

              {/* Proceed to Payment — enabled only after KOT/BOT has been issued.
                  Waiters (no Orders.Settle) don't bill — that's the cashier's job. */}
              {hasPermission(Permissions.Orders.Settle) && (
                <div className="pos-actions">
                  <button
                    className="pos-btn-charge"
                    disabled={orderItems.length === 0 || !kotUpToDate}
                    title={
                      orderItems.length === 0 ? undefined
                        : !kotIssued ? 'Print KOT / BOT first'
                        : hasUnsentChanges ? 'New items not sent — print KOT / BOT again'
                        : undefined
                    }
                    onClick={openSettle}
                  >
                    <CreditCard size={16} />
                    Proceed to Payment →
                  </button>
                </div>
              )}

              {/* Bottom icon buttons */}
              <div className="pos-bottom-btns">
                <button
                  className="pos-icon-btn"
                  // Allowed with an empty cart, or once every current item has been
                  // sent to the kitchen/bar (the order is persisted then and stays
                  // in Open Orders for later settlement).
                  disabled={orderItems.length > 0 && !kotUpToDate}
                  title={orderItems.length > 0 && !kotUpToDate ? 'Print KOT / BOT before starting a new order' : 'Start a new order'}
                  onClick={clearOrder}
                >
                  <Plus size={18} strokeWidth={1.5} />
                  <span className="pos-icon-btn-label">New Order</span>
                </button>
                {canIssueKot && (
                  <button
                    className="pos-icon-btn"
                    disabled={orderItems.length === 0 || saving || !hasUnsentChanges}
                    title={orderItems.length > 0 && !hasUnsentChanges ? 'All items already sent to kitchen/bar' : 'Print KOT / BOT'}
                    onClick={printKotBot}
                  >
                    <ChefHat size={18} strokeWidth={1.5} />
                    <span className="pos-icon-btn-label">{saving ? '…' : 'KOT / BOT'}</span>
                  </button>
                )}
                <button className="pos-icon-btn" disabled={orderItems.length === 0} onClick={printPreBill}>
                  <Printer size={18} strokeWidth={1.5} />
                  <span className="pos-icon-btn-label">Print Bill</span>
                </button>
                <button className="pos-icon-btn" onClick={() => { setShowOpenOrders(true); loadOpenOrders(); }}>
                  <ShoppingCart size={18} strokeWidth={1.5} />
                  <span className="pos-icon-btn-label">Open Orders</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════
            FLOOR PLAN TABLE PICKER OVERLAY
            ════════════════════════════════════════════════════════ */}
        {showFloorPlan && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={() => setShowFloorPlan(false)}
          >
            <div
              style={{ background: 'var(--ims-bg-card)', border: '1px solid var(--ims-border)', borderRadius: 14, width: '100%', maxWidth: 980, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ padding: '0.9rem 1.25rem', borderBottom: '1px solid var(--ims-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <LayoutGrid size={18} style={{ color: 'var(--ims-raw-orange-600)' }} />
                  <div>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ims-text-main)' }}>Floor Plan</h2>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--ims-text-sub)' }}>Click a table to select it</p>
                  </div>
                </div>
                <button onClick={() => setShowFloorPlan(false)} style={{ border: 'none', background: 'var(--ims-bg-page)', borderRadius: 8, cursor: 'pointer', padding: '4px 8px', color: 'var(--ims-text-sub)', display: 'flex', alignItems: 'center' }}>
                  <X size={18} />
                </button>
              </div>

              {/* Legend */}
              <div style={{ padding: '0.5rem 1.25rem', borderBottom: '1px solid var(--ims-border)', display: 'flex', gap: 20, flexShrink: 0, flexWrap: 'wrap' }}>
                {[
                  { label: 'Available', bg: '#dcfce7', border: '#16a34a', text: '#15803d' },
                  { label: 'Occupied',  bg: '#fee2e2', border: '#dc2626', text: '#b91c1c' },
                  { label: 'Reserved',  bg: '#fef9c3', border: '#ca8a04', text: '#a16207' },
                  { label: 'Selected',  bg: 'var(--ims-raw-orange-100)', border: 'var(--ims-raw-orange-600)', text: 'var(--ims-raw-orange-700)' },
                ].map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 3, background: s.bg, border: `2px solid ${s.border}`, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: 'var(--ims-text-sub)' }}>{s.label}</span>
                  </div>
                ))}
              </div>

              {/* Canvas */}
              <div style={{ overflow: 'auto', flex: 1, padding: '1rem' }}>
                {rawTables.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ims-text-muted)' }}>
                    <LayoutGrid size={48} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.4 }} />
                    <p>No tables found. Add tables in the Tables section first.</p>
                  </div>
                ) : (
                  <div style={{ position: 'relative', width: 900, height: 600, minWidth: 900, background: 'repeating-linear-gradient(0deg,transparent,transparent 29px,#e5e7eb 29px,#e5e7eb 30px),repeating-linear-gradient(90deg,transparent,transparent 29px,#e5e7eb 29px,#e5e7eb 30px)', backgroundSize: '30px 30px', borderRadius: 8, border: '1px solid var(--ims-border)' }}>
                    {rawTables.map((tbl: any) => {
                      const isSelected = tbl.id === modalTableId;
                      const isOccupied = tbl.liveStatus === 'Occupied';
                      const isReserved = tbl.liveStatus === 'Reserved';
                      const isRound = tbl.shape === 'Round';
                      const colors = isSelected
                        ? { bg: 'var(--ims-raw-orange-100)', border: 'var(--ims-raw-orange-600)', text: 'var(--ims-raw-orange-700)' }
                        : isOccupied
                          ? { bg: '#fee2e2', border: '#dc2626', text: '#b91c1c' }
                          : isReserved
                            ? { bg: '#fef9c3', border: '#ca8a04', text: '#a16207' }
                            : { bg: '#dcfce7', border: '#16a34a', text: '#15803d' };
                      const w = tbl.width  > 0 ? tbl.width  : 80;
                      const h = tbl.height > 0 ? tbl.height : 60;
                      return (
                        <div
                          key={tbl.id}
                          onClick={() => {
                            setModalTableId(tbl.id);
                            setModalTable(tbl.tableNumber);
                            if (tbl.capacity) setModalPax(tbl.capacity);
                            setShowFloorPlan(false);
                          }}
                          title={`${tbl.tableNumber} – ${tbl.name} (${tbl.capacity}p)${tbl.liveStatus ? ' · ' + tbl.liveStatus : ''}`}
                          style={{
                            position: 'absolute',
                            left: tbl.positionX ?? 0,
                            top: tbl.positionY ?? 0,
                            width: w, height: h,
                            background: colors.bg,
                            border: `2px solid ${colors.border}`,
                            borderRadius: isRound ? '50%' : 7,
                            cursor: 'pointer',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            userSelect: 'none',
                            boxShadow: isSelected ? `0 0 0 3px ${colors.border}55` : '0 1px 4px rgba(0,0,0,0.10)',
                            transition: 'box-shadow 0.12s, transform 0.12s',
                          }}
                          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.05)'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = ''; }}
                        >
                          <span style={{ fontSize: 11, fontWeight: 700, color: colors.text, lineHeight: 1.2, pointerEvents: 'none' }}>{tbl.tableNumber}</span>
                          <span style={{ fontSize: 9, color: colors.text, opacity: 0.8, pointerEvents: 'none' }}>{tbl.capacity}p</span>
                          {tbl.liveStatus === 'Occupied' && tbl.currentOrderNumber && (
                            <span style={{ fontSize: 8, color: colors.text, opacity: 0.7, pointerEvents: 'none', marginTop: 1 }}>{tbl.currentOrderNumber}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              {modalTableId > 0 && (
                <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid var(--ims-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: 'var(--ims-bg-page)' }}>
                  <span style={{ fontSize: 13, color: 'var(--ims-text-sub)' }}>
                    Selected: <strong style={{ color: 'var(--ims-text-main)' }}>{tableOptions.find(o => o.id === modalTableId)?.label ?? modalTable}</strong>
                  </span>
                  <button
                    className="pos-btn-charge"
                    style={{ padding: '6px 20px', fontSize: 13 }}
                    onClick={() => setShowFloorPlan(false)}
                  >
                    Confirm Table
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            OPEN ORDERS SLIDE-IN PANEL
            ════════════════════════════════════════════════════════ */}
        {showOpenOrders && (
          <div className="pos-open-orders-overlay" onClick={() => setShowOpenOrders(false)}>
            <div className="pos-open-orders-panel" onClick={e => e.stopPropagation()}>
              <div className="pos-open-orders-hdr">
                <div className="pos-open-orders-hdr-left">
                  <ShoppingCart size={18} />
                  <h3>Open Orders</h3>
                  {openOrders.length > 0 && <span className="pos-item-count">{openOrders.length}</span>}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="pos-header-icon-btn" onClick={loadOpenOrders} title="Refresh">
                    <RefreshCw size={15} />
                  </button>
                  <button className="pos-header-icon-btn" onClick={() => setShowOpenOrders(false)}>
                    <X size={16} />
                  </button>
                </div>
              </div>
              <div className="pos-open-orders-body">
                {openOrdersLoading ? (
                  <div className="pos-placeholder">Loading…</div>
                ) : openOrders.length === 0 ? (
                  <div className="pos-placeholder">No open orders</div>
                ) : (
                  openOrders.map(order => (
                    <div key={order.id} className="pos-open-order-row" onClick={() => resumeOrder(order)}>
                      <div className="pos-open-order-row-left">
                        <div className="pos-open-order-num">{order.orderNumber}</div>
                        <div className="pos-open-order-meta">
                          {order.tableNumber && <span><MapPin size={10} /> {order.tableNumber}</span>}
                          {order.numberOfPax && <span><Users size={10} /> {order.numberOfPax} Pax</span>}
                          <span>{order.orderType}</span>
                          <span>{order.lineCount} items</span>
                        </div>
                      </div>
                      <div className="pos-open-order-row-right">
                        <span className="pos-open-order-amt">{fmt.format(Number(order.totalAmount))}</span>
                        <span className="pos-open-order-time">{new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            BILL SETTLEMENT FULL-SCREEN PAGE
            ════════════════════════════════════════════════════════ */}
        {showSettle && (
          <div className="pos-settle-pg">

            <div className="pos-settle-pg-header">
              <div className="pos-settle-pg-header-left">
                <button className="pos-settle-back-btn" onClick={() => setShowSettle(false)}>
                  <ArrowLeft size={18} />
                </button>
                <div className="pos-settle-pg-hdr-icon"><FileText size={22} /></div>
                <div>
                  <h1 className="pos-settle-pg-title">Bill Settlement</h1>
                  <p className="pos-settle-pg-sub">Settle the bill and complete the transaction</p>
                </div>
              </div>
              <div className="pos-settle-pg-header-right">
                <div className="pos-settle-pg-pos-badge"><Monitor size={14} /> POS-01</div>
                <div className="pos-settle-pg-datetime">
                  <p>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  <p>{new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            </div>

            <div className="pos-settle-pg-body">

              {/* ── LEFT: Bill Summary ── */}
              <div className="pos-settle-pg-left">
                <div className="pos-settle-pg-card">
                  <div className="pos-bill-sum-header">
                    <div className="pos-bill-sum-title"><ShoppingCart size={17} /> Bill Summary</div>
                    <span className="pos-bill-inv-badge">
                      {activeOrderId ? `#${activeOrderId}` : '#NEW'}
                    </span>
                  </div>
                  <div className="pos-bill-customer-row">
                    <User size={14} />
                    {customerName || 'Walk-in Customer'}
                    {tableNumber && <span style={{ marginInlineStart: 'auto', fontSize: '0.75rem', color: 'var(--pos-text-sub)' }}><MapPin size={11} /> {tableNumber}</span>}
                  </div>

                  <table className="pos-bill-items-table">
                    <thead>
                      <tr>
                        <th>Item</th><th>Qty</th><th>Price</th><th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems.map(item => {
                        const lineNet = Math.max(0, item.price * item.quantity - item.discountAmount);
                        return (
                          <tr key={item.itemId}>
                            <td>{item.name}</td>
                            <td>{item.quantity}</td>
                            <td>{fmt.format(item.price)}</td>
                            <td>{fmt.format(lineNet)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="pos-bill-rows">
                    <div className="pos-bill-row"><span>Subtotal</span><span>{fmt.format(subtotal)}</span></div>
                    {orderDiscountAmt > 0 && (
                      <div className="pos-bill-row pos-bill-row-disc"><span>Discount</span><span>−{fmt.format(orderDiscountAmt)}</span></div>
                    )}
                    {taxAmt > 0 && <div className="pos-bill-row"><span>Tax</span><span>{fmt.format(taxAmt)}</span></div>}
                    {serviceChargeAmt > 0 && (
                      <div className="pos-bill-row">
                        <span>Service Charge ({selectedOutlet?.serviceChargePercent}%)</span>
                        <span>{fmt.format(serviceChargeAmt)}</span>
                      </div>
                    )}
                    {orderType === 'Delivery' && deliveryChargeAmt > 0 && (
                      <div className="pos-bill-row"><span>Delivery Charge</span><span>{fmt.format(deliveryChargeAmt)}</span></div>
                    )}
                  </div>

                  <div className="pos-bill-total-box">
                    <span className="pos-bill-total-label">Total Amount</span>
                    <span className="pos-bill-total-amount">{fmt.format(total)}</span>
                  </div>

                  <div className="pos-bill-stats">
                    <div className="pos-bill-stat">
                      <FileText size={18} strokeWidth={1.5} />
                      <span className="pos-bill-stat-label">Items</span>
                      <span className="pos-bill-stat-value">{orderItems.length}</span>
                    </div>
                    <div className="pos-bill-stat">
                      <ShoppingBag size={18} strokeWidth={1.5} />
                      <span className="pos-bill-stat-label">Total Qty</span>
                      <span className="pos-bill-stat-value">{itemCount}</span>
                    </div>
                  </div>

                  <div className="pos-bill-actions">
                    <button className="pos-bill-action-btn" onClick={printPreBill}>
                      <Printer size={15} /> Print Bill
                    </button>
                  </div>
                  <button className="pos-bill-cancel-btn" onClick={() => { setShowSettle(false); clearOrder(); }}>
                    <Trash2 size={15} /> Cancel Bill
                  </button>

                  <div className="pos-bill-footer">
                    <div className="pos-bill-footer-item">
                      <User size={14} />
                      <div>
                        <span className="pos-bill-footer-label">Cashier</span>
                        <span className="pos-bill-footer-val">Staff</span>
                      </div>
                    </div>
                    <div className="pos-bill-footer-item">
                      <ShoppingCart size={14} />
                      <div>
                        <span className="pos-bill-footer-label">Sales Type</span>
                        <span className="pos-bill-footer-val">{orderType}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── RIGHT: Payment ── */}
              <div className="pos-settle-pg-right">
                <div className="pos-settle-pg-right-scroll">

                  <div className="pos-settle-pg-card">
                    <div className="pos-settle-section-hdr">
                      <span className="pos-settle-section-icon"><Wallet size={16} /></span>
                      <h3>Amount Details</h3>
                    </div>
                    <div className="pos-amt-row">
                      <span>Total Amount</span>
                      <span style={{ fontWeight: 700, color: 'var(--pos-text-main)' }}>{fmtNum.format(total)}</span>
                    </div>
                    <div className="pos-amt-row">
                      <span>Paid Amount</span>
                      <span style={{ fontWeight: 600, color: totalPaid > 0 ? '#16a34a' : 'inherit' }}>{fmtNum.format(totalPaid)}</span>
                    </div>
                    {changeAmt > 0 ? (
                      <div className="pos-balance-box pos-balance-box--change">
                        <span className="pos-balance-label">Change to Return</span>
                        <span className="pos-balance-amount">{fmtNum.format(changeAmt)}</span>
                      </div>
                    ) : (
                      <div className="pos-balance-box">
                        <span className="pos-balance-label">Balance Due</span>
                        <span className="pos-balance-amount">{fmtNum.format(remaining)}</span>
                      </div>
                    )}
                  </div>

                  <div className="pos-settle-pg-card">
                    <div className="pos-settle-section-hdr">
                      <span className="pos-settle-section-icon"><CreditCard size={16} /></span>
                      <h3>Payment Methods</h3>
                    </div>
                    {paymentTypes.length === 0 ? (
                      <p style={{ fontSize: '0.75rem', color: 'var(--pos-text-muted)' }}>No payment types configured</p>
                    ) : (
                      <div className="pos-pay-method-grid">
                        {paymentTypes.map(pt => (
                          <button
                            key={pt.id}
                            className={`pos-pay-method-card${selPayMethod === pt.name ? ' active' : ''}`}
                            onClick={() => {
                              setSelPayMethod(pt.name);
                              setPayEntryAmount(remaining > 0 ? remaining.toFixed(2) : total.toFixed(2));
                            }}
                          >
                            <div className="pos-pay-method-logo" style={{ background: getPayMethodBg(pt.paymentCategory) }}>
                              {getPayMethodVisual(pt.name, pt.paymentCategory)}
                            </div>
                            <span className="pos-pay-method-card-name">{pt.name}</span>
                            {selPayMethod === pt.name && <span className="pos-pay-method-card-check"><Check size={9} /></span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {selPayMethod && (
                      <div className="pos-pay-entry-form">
                        <div className="pos-pay-entry-inputs">
                          <div style={{ flex: 1 }}>
                            <label className="pos-settle-form-label">Amount</label>
                            <input
                              className="pos-settle-amount-input"
                              type="number"
                              min="0"
                              step="0.01"
                              value={payEntryAmount}
                              onChange={e => setPayEntryAmount(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addPaymentEntry()}
                            />
                          </div>
                          <button className="btn-new" onClick={addPaymentEntry} disabled={!(parseFloat(payEntryAmount || '0') > 0)}>
                            <Plus size={14} /> Add
                          </button>
                        </div>
                        {isCash && quickAmounts.length > 0 && (
                          <div className="pos-quick-amounts">
                            {quickAmounts.map(a => (
                              <button key={a} className="pos-quick-amt-btn" onClick={() => setPayEntryAmount(a.toString())}>
                                {a.toLocaleString()}
                              </button>
                            ))}
                          </div>
                        )}
                        {!isCash && (
                          <input
                            className="pos-settle-ref-input"
                            style={{ marginTop: '0.5rem' }}
                            type="text"
                            placeholder="Reference / approval number (optional)"
                            value={payEntryRef}
                            onChange={e => setPayEntryRef(e.target.value)}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {paymentEntries.length > 0 && (
                    <div className="pos-settle-pg-card">
                      <div className="pos-settle-section-hdr">
                        <span className="pos-settle-section-icon"><FileText size={16} /></span>
                        <h3>Payment Breakdown</h3>
                      </div>
                      <div className="pos-pay-breakdown-list">
                        {paymentEntries.map((entry, i) => (
                          <div key={i} className="pos-pay-breakdown-row">
                            <span className="pos-pay-breakdown-icon">{getPayMethodVisual(entry.method, entry.category)}</span>
                            <span className="pos-pay-breakdown-method">{entry.method}</span>
                            {entry.reference && <span className="pos-pay-breakdown-ref">#{entry.reference}</span>}
                            <span className="pos-pay-breakdown-amt">{fmtNum.format(entry.amount)}</span>
                            <button className="pos-pay-breakdown-del" onClick={() => removePaymentEntry(i)}><X size={11} /></button>
                          </div>
                        ))}
                      </div>
                      <div className="pos-pay-breakdown-total">
                        <span>Total Paid</span>
                        <span>{fmtNum.format(totalPaid)}</span>
                      </div>
                    </div>
                  )}

                  {settleError && <div className="pos-settle-pg-error">{settleError}</div>}

                </div>

                <div className="pos-settle-bottom-bar">
                  <div className="pos-settle-balance-to-pay">
                    <div className="pos-settle-balance-icon"><Wallet size={20} /></div>
                    <div>
                      <span className="pos-settle-bp-label">{changeAmt > 0 ? 'Change Due' : 'Balance to Pay'}</span>
                      <span className="pos-settle-bp-amount">{fmtNum.format(changeAmt > 0 ? changeAmt : remaining)}</span>
                    </div>
                  </div>
                  <button
                    className="btn-new"
                    disabled={saving || paymentEntries.length === 0 || remaining > 0.005}
                    onClick={confirmSettle}
                  >
                    <Check size={18} />
                    {saving ? 'Processing…' : 'Settle Bill'}
                  </button>
                </div>

                <div className="pos-settle-secondary-actions">
                  <button className="pos-settle-sec-btn" onClick={printPreBill}>
                    <Eye size={14} /> Print Preview
                  </button>
                  <button className="pos-settle-sec-btn">
                    <Mail size={14} /> Email / SMS
                  </button>
                </div>
              </div>
            </div>

            <div className="pos-settle-pg-footer">
              <div className="pos-settle-pg-footer-left"><Lock size={12} /> Secure Transaction</div>
              <div className="pos-settle-pg-footer-right">Thank you! Have a nice day 😊</div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            VOID KOT ITEM MODAL
            ════════════════════════════════════════════════════════ */}
        {voidModalItem && (
          <div className="pos-open-orders-overlay" onClick={() => setVoidModalItem(null)}>
            <div className="pos-void-modal" onClick={e => e.stopPropagation()}>
              <div className="pos-void-modal-hdr">
                <X size={18} style={{ color: '#dc2626' }} />
                <h3>Void Sent Item</h3>
              </div>
              <p className="pos-void-modal-item">
                <strong>{voidModalItem.name}</strong>
                {' '}× {sentQtys[voidModalItem.itemId] ?? voidModalItem.quantity}
              </p>
              <p className="pos-void-modal-warning">
                This item has already been sent to the kitchen/bar. A Void KOT will be printed.
              </p>
              {kotVoidReasons.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {kotVoidReasons.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setVoidReason(r.reason)}
                      style={{
                        padding: '3px 10px',
                        borderRadius: 99,
                        border: `1px solid ${voidReason === r.reason ? '#dc2626' : 'var(--ims-border, #e5e7eb)'}`,
                        background: voidReason === r.reason ? '#fef2f2' : 'transparent',
                        color: voidReason === r.reason ? '#dc2626' : 'var(--ims-text-sub)',
                        fontSize: 12,
                        cursor: 'pointer',
                        fontWeight: voidReason === r.reason ? 600 : 400,
                      }}
                    >
                      {r.reason}
                    </button>
                  ))}
                </div>
              )}
              <label className="pos-void-modal-label">Void Reason *</label>
              <textarea
                className="pos-void-modal-textarea"
                rows={3}
                placeholder="Enter reason for voiding this item…"
                value={voidReason}
                onChange={e => setVoidReason(e.target.value)}
              />
              <div className="pos-void-modal-actions">
                <button
                  className="ims-btn ims-btn-secondary"
                  onClick={() => setVoidModalItem(null)}
                  disabled={voiding}
                >
                  Cancel
                </button>
                <button
                  className="pos-btn-charge"
                  style={{ background: 'linear-gradient(135deg,#dc2626,#ef4444)', fontSize: 13 }}
                  onClick={confirmVoidKotItem}
                  disabled={voiding || !voidReason.trim()}
                >
                  {voiding ? 'Voiding…' : 'Confirm Void & Print'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return cashierMode ? pageContent : <Layout>{pageContent}</Layout>;
};

export default RestaurantOrderPage;
